import {
  Button,
  Menu,
  MenuItem,
  notify,
  Switch,
  useConfirmModal,
} from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { AuthService } from '@affine/core/modules/cloud';
import { WorkspaceAreaCacheStore } from '@affine/core/modules/folder-api';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { MailFolder, MailSignatureScope } from '@affine/graphql';
import { type I18nInstance, useI18n } from '@affine/i18n';
import {
  ArrowDownSmallIcon,
  CheckBoxCheckLinearIcon,
  DeleteIcon,
  PlusIcon,
} from '@blocksuite/icons/rc';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo, useRef, useState } from 'react';

import { DEFAULT_PROVIDER_OPTION } from './mail-account-providers';
import { AccountWizard } from './mail-account-wizard';
import { mailText } from './mail-i18n';
import * as styles from './mail-settings.css';
import type { MailAccountInfo, MailUserQuotaInfo } from './types';
import { formatMailBytes } from './types';
import {
  useCurrentUserMailQuota,
  useWorkspaceMail,
} from './use-workspace-mail';

const SIGNATURE_SCOPES = [
  MailSignatureScope.new,
  MailSignatureScope.reply,
  MailSignatureScope.forward,
] as const;
const EMPTY_MAIL_ACCOUNTS: MailAccountInfo[] = [];
const DEFAULT_CALENDAR_MAIL_ACCOUNT_KEY_PREFIX =
  'streamlain:calendar-mail-default:v1';
const GLOBAL_MAIL_SETTINGS_TARGET_ID = '__global_mail_settings__';
const INHERIT_SIGNATURE_VALUE = '__inherit_global_signature__';

type MailSettingsMenuOption = {
  disabled?: boolean;
  label: string;
  value: string;
};

function mailAccountSyncBadge(
  i18n: I18nInstance,
  account: MailAccountInfo
): {
  className: string;
  label: string;
  title?: string;
} {
  if (!account.syncEnabled || account.status === 'disabled') {
    return {
      className: styles.accountBadge,
      label: mailText(i18n, 'settings.syncDisabled'),
    };
  }
  if (account.status === 'auth_error') {
    return {
      className: styles.accountBadgeError,
      label: mailText(i18n, 'settings.syncAuthError'),
      title: mailText(i18n, 'settings.syncAuthHelp'),
    };
  }
  if (account.status === 'sync_error' || account.lastError) {
    return {
      className: styles.accountBadgeError,
      label: mailText(i18n, 'settings.syncNeedsAttention'),
      title: mailText(i18n, 'settings.syncRetrying'),
    };
  }
  return {
    className: styles.accountBadge,
    label: mailText(i18n, 'settings.syncEnabled'),
  };
}

function MailSettingsMenuSelect({
  disabled,
  onChange,
  options,
  title,
  value,
}: {
  disabled?: boolean;
  onChange: (value: string) => void;
  options: MailSettingsMenuOption[];
  title?: string;
  value: string;
}) {
  const selectedOption = options.find(option => option.value === value);
  const label = selectedOption?.label ?? options[0]?.label ?? '';
  const items = useMemo(
    () =>
      options.map(option => {
        const itemProps = option.disabled
          ? { disabled: true }
          : { onSelect: () => onChange(option.value) };
        return (
          <MenuItem key={option.value} {...itemProps}>
            {option.label}
          </MenuItem>
        );
      }),
    [onChange, options]
  );

  return (
    <Menu contentOptions={{ align: 'start' }} items={items}>
      <button
        type="button"
        className={styles.selectButton}
        disabled={disabled || options.length === 0}
        title={title ?? label}
      >
        <span className={styles.selectButtonLabel}>{label}</span>
        <ArrowDownSmallIcon />
      </button>
    </Menu>
  );
}

function toDateTimeLocalValue(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function selectedMailAccount(
  accounts: MailAccountInfo[],
  selectedAccountId: string | null
): MailAccountInfo | null {
  return (
    accounts.find(account => account.id === selectedAccountId) ??
    accounts[0] ??
    null
  );
}

function mailAccountLabel(account: MailAccountInfo): string {
  return account.displayName && account.displayName !== account.emailAddress
    ? `${account.displayName} <${account.emailAddress}>`
    : account.emailAddress;
}

function defaultCalendarMailAccountKey(workspaceId: string): string {
  return `${DEFAULT_CALENDAR_MAIL_ACCOUNT_KEY_PREFIX}:${workspaceId}`;
}

function readDefaultCalendarMailAccountId(workspaceId: string): string | null {
  try {
    return (
      globalThis.localStorage?.getItem(
        defaultCalendarMailAccountKey(workspaceId)
      ) || null
    );
  } catch {
    return null;
  }
}

function writeDefaultCalendarMailAccountId(
  workspaceId: string,
  accountId: string | null
): void {
  try {
    const key = defaultCalendarMailAccountKey(workspaceId);
    if (!accountId) {
      globalThis.localStorage?.removeItem(key);
    } else {
      globalThis.localStorage?.setItem(key, accountId);
    }
  } catch {
    // Local preference storage must not block mail settings.
  }
}

type MailQuotaFactorInfo = MailUserQuotaInfo['factors'][number];

function MailQuotaProgress({
  percent,
  status,
}: {
  percent: number;
  status: string;
}) {
  const safePercent = Math.max(
    0,
    Math.min(100, Number.isFinite(percent) ? percent : 0)
  );
  return (
    <div className={styles.quotaProgress}>
      <div className={styles.quotaTrack}>
        <div
          className={styles.quotaFill}
          data-status={status}
          style={{ width: `${safePercent}%` }}
        />
      </div>
      <span className={styles.quotaPercent}>
        {Math.round(percent).toLocaleString()}%
      </span>
    </div>
  );
}

function MailQuotaFactorList({
  factors,
  i18n,
}: {
  factors: MailQuotaFactorInfo[];
  i18n: I18nInstance;
}) {
  return (
    <div className={styles.quotaFactorList}>
      {factors.map(factor => (
        <div className={styles.quotaFactorRow} key={factor.kind}>
          <div className={styles.accountText}>
            <span className={styles.accountName}>
              {formatMailQuotaFactorLabel(i18n, factor)}
            </span>
            <span className={styles.accountEmail}>
              {formatMailQuotaFactorDetail(i18n, factor)}
            </span>
          </div>
          <MailQuotaProgress percent={factor.percent} status={factor.status} />
        </div>
      ))}
    </div>
  );
}

function formatMailQuotaFactorLabel(
  i18n: I18nInstance,
  factor: MailQuotaFactorInfo
): string {
  switch (factor.kind) {
    case 'body_cache':
    case 'attachment_cache':
    case 'search_index':
    case 'indexed_messages':
    case 'search_window_coverage':
      return mailText(i18n, `settings.quotaFactor.${factor.kind}.label`);
    default:
      return factor.label;
  }
}

function formatMailQuotaFactorDetail(
  i18n: I18nInstance,
  factor: MailQuotaFactorInfo
): string {
  if (factor.usedBytes !== null && factor.limitBytes !== null) {
    return mailText(i18n, 'settings.quotaUsage.bytes', {
      used: formatMailBytes(factor.usedBytes),
      limit: formatMailBytes(factor.limitBytes),
    });
  }
  if (factor.usedCount !== null && factor.limitCount !== null) {
    return mailText(i18n, 'settings.quotaUsage.count', {
      used: factor.usedCount.toLocaleString(),
      limit: factor.limitCount.toLocaleString(),
    });
  }
  return factor.detail;
}

function formatMailProviderLabel(providerKind: string): string {
  switch (providerKind) {
    case 'gmail_oauth':
      return 'Gmail';
    case 'microsoft_oauth':
      return 'Microsoft';
    case 'custom_oauth2':
      return 'OAuth2';
    case 'generic_imap_smtp':
      return 'IMAP/SMTP';
    default:
      return providerKind;
  }
}

export function MailSettings({
  initialAccountId,
}: {
  initialAccountId?: string;
}) {
  const i18n = useI18n();
  const { openConfirmModal } = useConfirmModal();
  const workspaceService = useService(WorkspaceService);
  const workspaceAreaCache = useService(WorkspaceAreaCacheStore);
  const authService = useService(AuthService);
  const currentAccount = useLiveData(authService.session.account$);
  const workspaceId = workspaceService.workspace.id;
  const workspaceAreas = useLiveData(
    useMemo(
      () => LiveData.from(workspaceAreaCache.watchAreas(), []),
      [workspaceAreaCache]
    )
  );
  const [workspaceAreasError, setWorkspaceAreasError] = useState<string | null>(
    null
  );
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    initialAccountId ?? null
  );
  const [defaultAccountId, setDefaultAccountId] = useState<string | null>(null);
  const [settingsTargetId, setSettingsTargetId] = useState(
    initialAccountId ?? GLOBAL_MAIL_SETTINGS_TARGET_ID
  );
  const [showAccountWizard, setShowAccountWizard] = useState(false);
  const mail = useWorkspaceMail({
    workspaceId,
    accountId: selectedAccountId,
    folder: MailFolder.inbox,
    folderId: null,
    virtualFolder: null,
    selectedMessageId: null,
    messageLimit: 1,
    messageSearch: '',
    loadMessages: false,
  });
  useEffect(() => {
    let cancelled = false;
    setWorkspaceAreasError(null);
    void workspaceAreaCache.refresh().catch(err => {
      if (!cancelled) {
        setWorkspaceAreasError(
          err instanceof Error
            ? err.message
            : 'Workspace areas could not be loaded.'
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceAreaCache]);
  const accounts = mail.accounts ?? EMPTY_MAIL_ACCOUNTS;
  const ownedAccounts = accounts.filter(
    account => account.userId === currentAccount?.id
  );
  const mailQuota = useCurrentUserMailQuota(workspaceId);
  const selectedAccount = useMemo(
    () => selectedMailAccount(accounts, selectedAccountId),
    [accounts, selectedAccountId]
  );
  const selectedMailboxWorkspaceVisible =
    selectedAccount?.showInWorkspace ?? false;
  const canChangeSelectedMailboxWorkspaceVisibility =
    selectedAccount?.userId === currentAccount?.id;
  const canRepairSelectedAccount =
    selectedAccount?.userId === currentAccount?.id;
  const settingsTargetAccountId =
    settingsTargetId === GLOBAL_MAIL_SETTINGS_TARGET_ID
      ? null
      : settingsTargetId;
  const settingsTargetAccount = useMemo(
    () =>
      settingsTargetAccountId
        ? (accounts.find(account => account.id === settingsTargetAccountId) ??
          null)
        : null,
    [accounts, settingsTargetAccountId]
  );
  const settingsTargetIsAccount = Boolean(settingsTargetAccountId);
  const settingsTargetMissing =
    settingsTargetIsAccount && !settingsTargetAccount;
  const exactTargetAutoresponder =
    mail.autoresponders?.find(
      item => (item.accountId ?? null) === settingsTargetAccountId
    ) ?? null;
  const globalAutoresponder =
    mail.autoresponders?.find(item => item.accountId === null) ?? null;
  const selectedAutoresponder =
    exactTargetAutoresponder ??
    (settingsTargetIsAccount ? globalAutoresponder : null);
  const autoresponderInherited =
    settingsTargetIsAccount &&
    !exactTargetAutoresponder &&
    !!globalAutoresponder;
  const accountOptions = useMemo<MailSettingsMenuOption[]>(
    () =>
      accounts.map(account => ({
        label: mailAccountLabel(account),
        value: account.id,
      })),
    [accounts]
  );
  const settingsTargetOptions = useMemo<MailSettingsMenuOption[]>(
    () => [
      {
        label: mailText(i18n, 'settings.targetGlobal'),
        value: GLOBAL_MAIL_SETTINGS_TARGET_ID,
      },
      ...accounts.map(account => ({
        label: mailText(i18n, 'settings.targetAccount', {
          account: mailAccountLabel(account),
        }),
        value: account.id,
      })),
    ],
    [accounts, i18n]
  );
  const editSignatureOptions = useMemo<MailSettingsMenuOption[]>(
    () => [
      {
        label: mailText(i18n, 'settings.newSignature'),
        value: '',
      },
      ...(mail.signatures ?? []).map(signature => ({
        label: signature.name,
        value: signature.id,
      })),
    ],
    [i18n, mail.signatures]
  );
  const signatureById = useMemo(
    () =>
      new Map(
        (mail.signatures ?? []).map(signature => [signature.id, signature])
      ),
    [mail.signatures]
  );
  const activeSignatureOptions = useMemo<MailSettingsMenuOption[]>(
    () => [
      {
        label: mailText(i18n, 'settings.noSignature'),
        value: '',
      },
      ...(mail.signatures ?? [])
        .filter(signature => signature.isEnabled)
        .map(signature => ({
          label: signature.name,
          value: signature.id,
        })),
    ],
    [i18n, mail.signatures]
  );
  const signatureOptionsForValue = (
    value: string,
    includeInherit: boolean
  ): MailSettingsMenuOption[] => {
    const options = includeInherit
      ? [
          {
            label: mailText(i18n, 'settings.inheritGlobalDefault'),
            value: INHERIT_SIGNATURE_VALUE,
          },
          ...activeSignatureOptions,
        ]
      : activeSignatureOptions;
    if (!value || value === INHERIT_SIGNATURE_VALUE) return options;
    if (options.some(option => option.value === value)) return options;
    const signature = signatureById.get(value);
    if (!signature) return options;
    return [
      ...options,
      {
        disabled: true,
        label: mailText(i18n, 'settings.disabledSignatureLabel', {
          name: signature.name,
        }),
        value,
      },
    ];
  };
  const isLoading =
    mail.accountsLoading ||
    mail.signaturesLoading ||
    mail.signatureAssignmentsLoading ||
    mail.autorespondersLoading;
  const [editingSignatureId, setEditingSignatureId] = useState<string | null>(
    null
  );
  const editingSignature =
    mail.signatures?.find(signature => signature.id === editingSignatureId) ??
    null;
  const [signatureName, setSignatureName] = useState('');
  const [signatureHtml, setSignatureHtml] = useState('');
  const [signatureText, setSignatureText] = useState('');
  const [signatureEnabled, setSignatureEnabled] = useState(true);
  const [autoresponderEnabled, setAutoresponderEnabled] = useState(false);
  const [autoresponderStart, setAutoresponderStart] = useState('');
  const [autoresponderEnd, setAutoresponderEnd] = useState('');
  const [autoresponderSubject, setAutoresponderSubject] = useState('');
  const [autoresponderBody, setAutoresponderBody] = useState('');
  const [autoresponderSignatureId, setAutoresponderSignatureId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [reindexingAccountId, setReindexingAccountId] = useState<string | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [signatureEditorOpen, setSignatureEditorOpen] = useState(false);

  useEffect(() => {
    if (!accounts.length) {
      setDefaultAccountId(null);
      writeDefaultCalendarMailAccountId(workspaceId, null);
      return;
    }
    const accountIds = new Set(accounts.map(account => account.id));
    const storedAccountId = readDefaultCalendarMailAccountId(workspaceId);
    const nextDefaultAccountId =
      storedAccountId && accountIds.has(storedAccountId)
        ? storedAccountId
        : accounts[0]!.id;
    if (nextDefaultAccountId !== storedAccountId) {
      writeDefaultCalendarMailAccountId(workspaceId, nextDefaultAccountId);
    }
    setDefaultAccountId(nextDefaultAccountId);
    setSelectedAccountId(current =>
      current && accountIds.has(current) ? current : nextDefaultAccountId
    );
  }, [accounts, workspaceId]);

  useEffect(() => {
    if (!accounts.length || settingsTargetId === GLOBAL_MAIL_SETTINGS_TARGET_ID)
      return;
    const accountExists = accounts.some(
      account => account.id === settingsTargetId
    );
    if (!accountExists) {
      setSettingsTargetId(GLOBAL_MAIL_SETTINGS_TARGET_ID);
    }
  }, [accounts, settingsTargetId]);

  useEffect(() => {
    if (!editingSignature) {
      setSignatureName('');
      setSignatureHtml('');
      setSignatureText('');
      setSignatureEnabled(true);
      return;
    }
    setSignatureName(editingSignature.name);
    setSignatureHtml(editingSignature.bodyHtml ?? '');
    setSignatureText(editingSignature.bodyText ?? '');
    setSignatureEnabled(editingSignature.isEnabled);
  }, [editingSignature]);

  useEffect(() => {
    setAutoresponderEnabled(selectedAutoresponder?.enabled ?? false);
    setAutoresponderStart(
      toDateTimeLocalValue(selectedAutoresponder?.startsAt)
    );
    setAutoresponderEnd(toDateTimeLocalValue(selectedAutoresponder?.endsAt));
    setAutoresponderSubject(selectedAutoresponder?.subject ?? '');
    setAutoresponderBody(
      selectedAutoresponder?.bodyHtml ?? selectedAutoresponder?.bodyText ?? ''
    );
    setAutoresponderSignatureId(selectedAutoresponder?.signatureId ?? '');
  }, [selectedAutoresponder]);

  const saveSignature = async () => {
    setError(null);
    setIsSaving(true);
    try {
      const saved = await mail.upsertSignature({
        workspaceId,
        signatureId: editingSignatureId,
        name: signatureName.trim(),
        bodyHtml: signatureHtml || null,
        bodyText: signatureText || null,
        isEnabled: signatureEnabled,
      });
      setEditingSignatureId(saved.id);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mailText(i18n, 'settings.saveFailed')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSignature = async () => {
    if (!editingSignatureId) return;
    setError(null);
    setIsSaving(true);
    try {
      await mail.deleteSignature(editingSignatureId);
      setEditingSignatureId(null);
      setSignatureEditorOpen(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mailText(i18n, 'settings.saveFailed')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const assignSignature = async (
    scope: MailSignatureScope,
    signatureId: string
  ) => {
    if (settingsTargetIsAccount && !settingsTargetAccount) return;
    setError(null);
    try {
      await mail.assignSignature({
        workspaceId,
        accountId: settingsTargetAccountId,
        scope,
        signatureId: signatureId || null,
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mailText(i18n, 'settings.saveFailed')
      );
    }
  };

  const resetSignatureOverride = async (scope: MailSignatureScope) => {
    if (!settingsTargetAccountId) return;
    setError(null);
    try {
      await mail.deleteSignatureAssignment(settingsTargetAccountId, scope);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mailText(i18n, 'settings.saveFailed')
      );
    }
  };

  const saveAutoresponder = async () => {
    if (settingsTargetIsAccount && !settingsTargetAccount) return;
    setError(null);
    setIsSaving(true);
    try {
      await mail.upsertAutoresponder({
        workspaceId,
        accountId: settingsTargetAccountId,
        enabled: autoresponderEnabled,
        startsAt: autoresponderStart
          ? new Date(autoresponderStart).toISOString()
          : null,
        endsAt: autoresponderEnd
          ? new Date(autoresponderEnd).toISOString()
          : null,
        subject: autoresponderSubject || null,
        bodyHtml: autoresponderBody || null,
        bodyText: autoresponderBody || null,
        signatureId: autoresponderSignatureId || null,
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mailText(i18n, 'settings.saveFailed')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const resetAutoresponderOverride = async () => {
    if (!settingsTargetAccountId) return;
    setError(null);
    setIsSaving(true);
    try {
      await mail.deleteAutoresponderOverride(settingsTargetAccountId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mailText(i18n, 'settings.saveFailed')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const deleteAccount = () => {
    if (!selectedAccount) return;
    const account = selectedAccount;
    openConfirmModal({
      title: mailText(i18n, 'confirm.deleteAccount', {
        account: account.emailAddress,
      }),
      confirmText: i18n['Remove']?.() ?? mailText(i18n, 'action.delete'),
      cancelText: i18n['Cancel'](),
      confirmButtonOptions: {
        variant: 'error',
      },
      onConfirm: async () => {
        setError(null);
        setIsSaving(true);
        try {
          await mail.deleteAccount(account.id);
          setSelectedAccountId(null);
        } catch (err) {
          setError(
            err instanceof Error
              ? err.message
              : mailText(i18n, 'settings.saveFailed')
          );
        } finally {
          setIsSaving(false);
        }
      },
    });
  };

  const rebuildSelectedAccountSearchIndex = async () => {
    if (!selectedAccount) return;
    setError(null);
    setReindexingAccountId(selectedAccount.id);
    try {
      const result = await mailQuota.rebuildAccountSearchIndex(
        selectedAccount.id
      );
      if (result) {
        notify.success({
          title: i18n.t(
            'com.streamlain.settings.workspace.mail.search.refreshStarted'
          ),
          message: i18n.t(
            'com.streamlain.settings.workspace.mail.search.refreshStartedDesc'
          ),
        });
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mailText(i18n, 'settings.saveFailed')
      );
    } finally {
      setReindexingAccountId(null);
    }
  };

  const changeDefaultAccount = (accountId: string) => {
    setDefaultAccountId(accountId);
    writeDefaultCalendarMailAccountId(workspaceId, accountId || null);
    if (accountId && !selectedAccountId) {
      setSelectedAccountId(accountId);
    }
  };

  const changeMailboxWorkspaceVisible = async (visible: boolean) => {
    if (!selectedAccount || !canChangeSelectedMailboxWorkspaceVisibility) {
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await mail.updateAccount({
        workspaceId,
        accountId: selectedAccount.id,
        showInWorkspace: visible,
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mailText(i18n, 'settings.saveFailed')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const renderAutoresponderStatus = () => {
    if (autoresponderInherited) {
      return mailText(
        i18n,
        autoresponderEnabled
          ? 'settings.autoresponderStatusOnInherited'
          : 'settings.autoresponderStatusOffInherited'
      );
    }
    if (settingsTargetIsAccount) {
      return mailText(
        i18n,
        autoresponderEnabled
          ? 'settings.autoresponderStatusOnAccount'
          : 'settings.autoresponderStatusOffAccount'
      );
    }
    return mailText(
      i18n,
      autoresponderEnabled
        ? 'settings.autoresponderStatusOnGlobal'
        : 'settings.autoresponderStatusOffGlobal'
    );
  };

  const quotaCoverageFactor = mailQuota.quota?.factors.find(
    factor => factor.kind === 'search_window_coverage'
  );
  const quotaCoverageLabel = quotaCoverageFactor
    ? mailText(i18n, 'settings.quotaSearchable', {
        used: (quotaCoverageFactor.usedCount ?? 0).toLocaleString(),
        limit: (quotaCoverageFactor.limitCount ?? 0).toLocaleString(),
      })
    : null;
  const quotaCoverageIncomplete = Boolean(
    quotaCoverageFactor?.limitCount &&
    (quotaCoverageFactor.usedCount ?? 0) < quotaCoverageFactor.limitCount
  );
  const [quotaCoverageDelayed, setQuotaCoverageDelayed] = useState(false);
  const quotaCoverageProgressRef = useRef({
    usedCount: -1,
    changedAt: Date.now(),
  });
  useEffect(() => {
    const usedCount = quotaCoverageFactor?.usedCount ?? 0;
    if (!quotaCoverageIncomplete) {
      quotaCoverageProgressRef.current = {
        usedCount,
        changedAt: Date.now(),
      };
      setQuotaCoverageDelayed(false);
      return;
    }
    if (quotaCoverageProgressRef.current.usedCount !== usedCount) {
      quotaCoverageProgressRef.current = {
        usedCount,
        changedAt: Date.now(),
      };
      setQuotaCoverageDelayed(false);
    }
    const delayRemaining = Math.max(
      0,
      150_000 - (Date.now() - quotaCoverageProgressRef.current.changedAt)
    );
    const timer = window.setTimeout(
      () => setQuotaCoverageDelayed(true),
      delayRemaining
    );
    return () => window.clearTimeout(timer);
  }, [quotaCoverageFactor?.usedCount, quotaCoverageIncomplete]);
  const incompleteQuotaAccountIds = new Set(
    (mailQuota.quota?.accounts ?? [])
      .filter(account => {
        const coverage = account.factors.find(
          factor => factor.kind === 'search_window_coverage'
        );
        return Boolean(
          coverage?.limitCount &&
          (coverage.usedCount ?? 0) < coverage.limitCount
        );
      })
      .map(account => account.accountId)
  );
  const quotaCoverageHasSyncError = ownedAccounts.some(
    account =>
      incompleteQuotaAccountIds.has(account.id) &&
      (account.status === 'auth_error' ||
        account.status === 'sync_error' ||
        Boolean(account.lastError))
  );
  const quotaCoverageSyncPaused = ownedAccounts.some(
    account =>
      incompleteQuotaAccountIds.has(account.id) &&
      (!account.syncEnabled || account.status === 'disabled')
  );
  const quotaCoverageState = !quotaCoverageIncomplete
    ? 'complete'
    : quotaCoverageHasSyncError
      ? 'error'
      : quotaCoverageSyncPaused
        ? 'paused'
        : quotaCoverageDelayed
          ? 'delayed'
          : 'indexing';
  const quotaCoverageStateLabel =
    quotaCoverageState === 'error'
      ? mailText(i18n, 'settings.quotaState.error')
      : quotaCoverageState === 'paused'
        ? mailText(i18n, 'settings.quotaState.paused')
        : quotaCoverageState === 'delayed'
          ? mailText(i18n, 'settings.quotaState.delayed')
          : quotaCoverageState === 'indexing'
            ? mailText(i18n, 'settings.quotaState.indexing')
            : mailText(i18n, 'settings.quotaState.complete');
  const quotaCoverageProgressStatus =
    quotaCoverageState === 'error'
      ? 'danger'
      : quotaCoverageState === 'paused' || quotaCoverageState === 'delayed'
        ? 'warn'
        : 'progress';

  const quotaSettingsSection = accounts.length ? (
    <SettingWrapper title={mailText(i18n, 'settings.quotaTitle')}>
      <SettingRow
        name={mailText(i18n, 'settings.quotaScope')}
        desc={mailText(i18n, 'settings.quotaScopeDesc')}
        spreadCol={false}
      >
        <div className={styles.rowContent}>
          {mailQuota.isLoading ? (
            <div className={styles.statusText}>{mailText(i18n, 'loading')}</div>
          ) : mailQuota.quota ? (
            <div className={styles.quotaPanel}>
              <div className={styles.quotaSummary}>
                <div className={styles.quotaSummaryCopy}>
                  <span className={styles.quotaSummaryTitle}>
                    {quotaCoverageLabel}
                  </span>
                  <span
                    className={styles.quotaStateText}
                    data-status={quotaCoverageState}
                  >
                    {quotaCoverageStateLabel}
                  </span>
                </div>
                {quotaCoverageFactor ? (
                  <MailQuotaProgress
                    percent={quotaCoverageFactor.percent}
                    status={quotaCoverageProgressStatus}
                  />
                ) : null}
              </div>
              <MailQuotaFactorList
                factors={mailQuota.quota.factors.filter(
                  factor =>
                    factor.kind === 'body_cache' ||
                    factor.kind === 'attachment_cache'
                )}
                i18n={i18n}
              />
              <div className={styles.quotaPolicyLine}>
                {mailText(i18n, 'settings.quotaPolicySummary', {
                  days: String(mailQuota.quota.searchIndexWindowDays),
                  messages:
                    mailQuota.quota.searchIndexMaxMessagesPerUser.toLocaleString(),
                  bodyDays: String(mailQuota.quota.bodyCacheTtlDays),
                  attachmentDays: String(
                    mailQuota.quota.attachmentCacheTtlDays
                  ),
                })}
              </div>
              {mailQuota.quota.accounts.length > 1 ? (
                <div className={styles.quotaAccountList}>
                  {mailQuota.quota.accounts.map(account => {
                    const coverage = account.factors.find(
                      factor => factor.kind === 'search_window_coverage'
                    );
                    return (
                      <div
                        className={styles.quotaAccountItem}
                        key={account.accountId}
                      >
                        <div className={styles.quotaAccountButton}>
                          <span className={styles.accountText}>
                            <span className={styles.accountName}>
                              {account.emailAddress}
                            </span>
                            <span className={styles.accountEmail}>
                              {formatMailProviderLabel(account.providerKind)}
                            </span>
                          </span>
                          <span className={styles.quotaAccountMetric}>
                            {mailText(i18n, 'settings.quotaAccountSearchable', {
                              used: (coverage?.usedCount ?? 0).toLocaleString(),
                              limit: (
                                coverage?.limitCount ?? 0
                              ).toLocaleString(),
                            })}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : (
            <div className={styles.statusText}>
              {mailText(i18n, 'settings.quotaUnavailable')}
            </div>
          )}
        </div>
      </SettingRow>
    </SettingWrapper>
  ) : null;

  return (
    <>
      <SettingHeader
        title={mailText(i18n, 'settings.title')}
        subtitle={mailText(i18n, 'settings.subtitle')}
      />

      {showAccountWizard ? (
        <AccountWizard
          workspaceId={workspaceId}
          workspaceAreas={workspaceAreas}
          workspaceAreasError={workspaceAreasError}
          initialProvider={DEFAULT_PROVIDER_OPTION}
          showProviderPicker={true}
          overlayStyle={{ position: 'fixed' }}
          onClose={() => setShowAccountWizard(false)}
          onConnect={mail.connectAccount}
        />
      ) : null}

      <SettingWrapper title={mailText(i18n, 'settings.accounts')}>
        <SettingRow
          name={mailText(i18n, 'settings.connectedAccounts')}
          desc={mailText(i18n, 'settings.accountsDesc')}
          spreadCol={false}
        >
          <div className={styles.rowContent}>
            {isLoading ? (
              <div className={styles.statusText}>
                {mailText(i18n, 'loading')}
              </div>
            ) : accounts.length ? (
              <>
                <div className={styles.accountList}>
                  {accounts.map(account => {
                    const syncBadge = mailAccountSyncBadge(i18n, account);
                    return (
                      <button
                        key={account.id}
                        type="button"
                        className={
                          account.id === selectedAccount?.id
                            ? styles.accountButtonActive
                            : styles.accountButton
                        }
                        onClick={() => {
                          setSelectedAccountId(account.id);
                        }}
                      >
                        <span className={styles.accountText}>
                          <span className={styles.accountName}>
                            {account.displayName || account.emailAddress}
                          </span>
                          <span className={styles.accountEmail}>
                            {account.emailAddress}
                          </span>
                        </span>
                        <span
                          className={syncBadge.className}
                          title={syncBadge.title}
                        >
                          {syncBadge.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className={styles.actionRow}>
                  <Button
                    variant="primary"
                    prefix={<PlusIcon />}
                    onClick={() => setShowAccountWizard(true)}
                  >
                    {mailText(i18n, 'settings.addAccount')}
                  </Button>
                  {selectedAccount ? (
                    <>
                      {canRepairSelectedAccount ? (
                        <Button
                          disabled={
                            reindexingAccountId === selectedAccount.id ||
                            isSaving
                          }
                          onClick={() =>
                            void rebuildSelectedAccountSearchIndex()
                          }
                        >
                          {reindexingAccountId === selectedAccount.id
                            ? i18n.t(
                                'com.streamlain.settings.workspace.mail.actions.reindexQueued'
                              )
                            : i18n.t(
                                'com.streamlain.settings.workspace.mail.actions.reindex'
                              )}
                        </Button>
                      ) : null}
                      <Button
                        variant="error"
                        prefix={<DeleteIcon />}
                        disabled={isSaving}
                        onClick={deleteAccount}
                      >
                        {mailText(i18n, 'settings.deleteAccount')}
                      </Button>
                    </>
                  ) : null}
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.statusText}>
                  {mailText(i18n, 'settings.noAccounts')}
                </div>
                <Button
                  variant="primary"
                  prefix={<PlusIcon />}
                  onClick={() => setShowAccountWizard(true)}
                >
                  {mailText(i18n, 'settings.addAccount')}
                </Button>
              </div>
            )}
          </div>
        </SettingRow>
        {selectedAccount ? (
          <SettingRow
            name={mailText(i18n, 'settings.mailboxWorkspaceVisible', {
              account: mailAccountLabel(selectedAccount),
            })}
            desc={mailText(i18n, 'settings.mailboxWorkspaceVisibleDesc')}
          >
            <Switch
              checked={selectedMailboxWorkspaceVisible}
              disabled={
                isSaving || !canChangeSelectedMailboxWorkspaceVisibility
              }
              onChange={visible => void changeMailboxWorkspaceVisible(visible)}
            />
          </SettingRow>
        ) : null}
        {accounts.length ? (
          <SettingRow
            name={mailText(i18n, 'settings.defaultAccount')}
            desc={mailText(i18n, 'settings.defaultAccountDesc')}
            spreadCol={false}
          >
            <div className={styles.rowContent}>
              <MailSettingsMenuSelect
                value={defaultAccountId ?? ''}
                options={accountOptions}
                onChange={changeDefaultAccount}
              />
            </div>
          </SettingRow>
        ) : null}
      </SettingWrapper>

      {quotaSettingsSection}

      <SettingWrapper title={mailText(i18n, 'settings.defaults')}>
        {accounts.length ? (
          <SettingRow
            name={mailText(i18n, 'settings.defaultsTarget')}
            desc={mailText(i18n, 'settings.defaultsTargetDesc')}
            spreadCol={false}
          >
            <div className={styles.rowContent}>
              <MailSettingsMenuSelect
                value={settingsTargetId}
                options={settingsTargetOptions}
                onChange={setSettingsTargetId}
              />
              <div className={styles.contextHint}>
                {settingsTargetIsAccount
                  ? mailText(i18n, 'settings.targetAccountHint', {
                      account: settingsTargetAccount
                        ? mailAccountLabel(settingsTargetAccount)
                        : '',
                    })
                  : mailText(i18n, 'settings.targetGlobalHint')}
              </div>
            </div>
          </SettingRow>
        ) : null}
        <SettingRow
          name={mailText(i18n, 'settings.manageSignatures')}
          desc={mailText(i18n, 'settings.manageSignaturesDesc')}
          spreadCol={false}
        >
          <div className={styles.rowContent}>
            {signatureEditorOpen ? (
              <>
                <MailSettingsMenuSelect
                  value={editingSignatureId ?? ''}
                  options={editSignatureOptions}
                  onChange={value => setEditingSignatureId(value || null)}
                />
                <div className={styles.fieldGrid}>
                  <label className={styles.field}>
                    {mailText(i18n, 'settings.signatureName')}
                    <input
                      className={styles.input}
                      type="text"
                      value={signatureName}
                      onChange={event => setSignatureName(event.target.value)}
                    />
                  </label>
                  <SettingRow
                    name={mailText(i18n, 'settings.signatureEnabled')}
                    spreadCol
                    style={{ marginBottom: 0 }}
                  >
                    <Switch
                      checked={signatureEnabled}
                      onChange={setSignatureEnabled}
                    />
                  </SettingRow>
                  <label className={styles.fieldFull}>
                    {mailText(i18n, 'settings.signatureHtml')}
                    <textarea
                      className={styles.textarea}
                      value={signatureHtml}
                      onChange={event => setSignatureHtml(event.target.value)}
                    />
                  </label>
                  <label className={styles.fieldFull}>
                    {mailText(i18n, 'settings.signatureText')}
                    <textarea
                      className={styles.textareaSmall}
                      value={signatureText}
                      onChange={event => setSignatureText(event.target.value)}
                    />
                  </label>
                </div>
                <div className={styles.actionRow}>
                  <Button
                    variant="primary"
                    prefix={<CheckBoxCheckLinearIcon />}
                    disabled={isSaving || !signatureName.trim()}
                    onClick={() => void saveSignature()}
                  >
                    {mailText(i18n, 'action.save')}
                  </Button>
                  {editingSignatureId ? (
                    <Button
                      variant="error"
                      prefix={<DeleteIcon />}
                      disabled={isSaving}
                      onClick={() => void deleteSignature()}
                    >
                      {mailText(i18n, 'action.delete')}
                    </Button>
                  ) : null}
                  <Button
                    disabled={isSaving}
                    onClick={() => setSignatureEditorOpen(false)}
                  >
                    {mailText(i18n, 'action.close')}
                  </Button>
                </div>
              </>
            ) : (
              <div className={styles.actionRow}>
                <MailSettingsMenuSelect
                  value={editingSignatureId ?? ''}
                  options={editSignatureOptions}
                  onChange={value => setEditingSignatureId(value || null)}
                />
                <Button onClick={() => setSignatureEditorOpen(true)}>
                  {mailText(i18n, 'settings.editSignature')}
                </Button>
              </div>
            )}
          </div>
        </SettingRow>

        <SettingRow
          name={mailText(i18n, 'settings.signatureAssignments')}
          desc={mailText(i18n, 'settings.signatureAssignmentsDesc')}
          spreadCol={false}
        >
          <div className={styles.rowContent}>
            <div className={styles.fieldGrid3}>
              {SIGNATURE_SCOPES.map(scope => {
                const assignment = mail.signatureAssignments?.find(
                  item =>
                    (item.accountId ?? null) === settingsTargetAccountId &&
                    item.scope === scope
                );
                const value =
                  settingsTargetIsAccount && !assignment
                    ? INHERIT_SIGNATURE_VALUE
                    : (assignment?.signatureId ?? '');
                return (
                  <label key={scope} className={styles.field}>
                    {mailText(i18n, `settings.signatureScope.${scope}`)}
                    <MailSettingsMenuSelect
                      value={value}
                      disabled={
                        settingsTargetIsAccount && !settingsTargetAccount
                      }
                      options={signatureOptionsForValue(
                        value,
                        settingsTargetIsAccount
                      )}
                      onChange={nextValue => {
                        if (nextValue === INHERIT_SIGNATURE_VALUE) {
                          void resetSignatureOverride(scope);
                          return;
                        }
                        void assignSignature(scope, nextValue);
                      }}
                    />
                  </label>
                );
              })}
            </div>
            {settingsTargetIsAccount ? (
              <div className={styles.contextHint}>
                {mailText(i18n, 'settings.signatureOverrideHint')}
              </div>
            ) : null}
          </div>
        </SettingRow>
        <SettingRow
          name={mailText(i18n, 'settings.autoresponderEnabled')}
          desc={renderAutoresponderStatus()}
          disabled={settingsTargetMissing}
        >
          <Switch
            checked={autoresponderEnabled}
            disabled={settingsTargetMissing}
            onChange={setAutoresponderEnabled}
          />
        </SettingRow>
        {autoresponderEnabled && !settingsTargetMissing ? (
          <SettingRow
            name={mailText(i18n, 'settings.autoresponderDesc')}
            spreadCol={false}
          >
            <div className={styles.rowContent}>
              {autoresponderInherited ? (
                <div className={styles.contextHint}>
                  {mailText(i18n, 'settings.autoresponderInheritedHint')}
                </div>
              ) : null}
              <div className={styles.fieldGrid}>
                <label className={styles.field}>
                  {mailText(i18n, 'settings.autoresponderStart')}
                  <input
                    className={styles.input}
                    type="datetime-local"
                    value={autoresponderStart}
                    onChange={event =>
                      setAutoresponderStart(event.target.value)
                    }
                  />
                </label>
                <label className={styles.field}>
                  {mailText(i18n, 'settings.autoresponderEnd')}
                  <input
                    className={styles.input}
                    type="datetime-local"
                    value={autoresponderEnd}
                    onChange={event => setAutoresponderEnd(event.target.value)}
                  />
                </label>
                <label className={styles.fieldFull}>
                  {mailText(i18n, 'settings.autoresponderSubject')}
                  <input
                    className={styles.input}
                    type="text"
                    value={autoresponderSubject}
                    onChange={event =>
                      setAutoresponderSubject(event.target.value)
                    }
                  />
                </label>
                <label className={styles.fieldFull}>
                  {mailText(i18n, 'settings.autoresponderBody')}
                  <textarea
                    className={styles.textarea}
                    value={autoresponderBody}
                    onChange={event => setAutoresponderBody(event.target.value)}
                  />
                </label>
                <label className={styles.fieldFull}>
                  {mailText(i18n, 'settings.autoresponderSignature')}
                  <MailSettingsMenuSelect
                    value={autoresponderSignatureId}
                    options={signatureOptionsForValue(
                      autoresponderSignatureId,
                      false
                    )}
                    onChange={setAutoresponderSignatureId}
                  />
                </label>
              </div>
              <div className={styles.actionRow}>
                <Button
                  variant="primary"
                  prefix={<CheckBoxCheckLinearIcon />}
                  disabled={isSaving}
                  onClick={() => void saveAutoresponder()}
                >
                  {mailText(i18n, 'action.save')}
                </Button>
                {settingsTargetIsAccount && exactTargetAutoresponder ? (
                  <Button
                    disabled={isSaving}
                    onClick={() => void resetAutoresponderOverride()}
                  >
                    {mailText(i18n, 'settings.resetOverride')}
                  </Button>
                ) : null}
              </div>
            </div>
          </SettingRow>
        ) : null}
      </SettingWrapper>

      {error ? <div className={styles.errorText}>{error}</div> : null}
    </>
  );
}
